// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { buildEvent, formatAmount } from '../../lib/eventLog.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import { formatDate } from '../../lib/formatLocale.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { ReconcileMark } from '../ui/ReconcileMark.js';
import { CloseIcon } from './icons.js';
import { hapticMedium, hapticSuccess } from '../../native/haptics.js';
import type { ReconciliationPostedPayload } from '../../types.js';

interface MobileReconcileProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

// A single unreconciled clearing item, projected from the ledger.
interface ClearingItem {
  txId: string;
  txDate: string;
  txDesc: string;
  txNum: string;
  splitId: string;
  amount: number;
  cleared: boolean;
}

// DECISION (Phase 8): the mobile reconcile is a full-screen, single-column flow.
// It reuses the DESKTOP ReconcileView logic exactly — the same local `checkedSplitIds`
// Set drives the running difference, and Finish emits ONE `ReconciliationPosted`
// event with the identical `ReconciliationPostedPayload`. Toggling cleared state is
// LOCAL UI state (never an event) until Finish, matching ReconcileView.
export function MobileReconcile({ worker, keyData }: MobileReconcileProps) {
  const { t } = useTranslation(['mobile', 'accounting', 'transactions', 'common', 'errors']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const account = id ? ledger.accounts.get(id) : undefined;

  const [step, setStep] = useState<1 | 2>(1);
  const [statementDate, setStatementDate] = useState(new Date().toISOString().slice(0, 10));
  const [statementBalance, setStatementBalance] = useState('');
  const [checkedSplitIds, setCheckedSplitIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const currency = account?.currency ?? 'USD';

  // Project all unreconciled splits (reconcileState !== 'y') for this account.
  const items = useMemo<ClearingItem[]>(() => {
    if (!id) return [];
    const list: ClearingItem[] = [];
    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      for (const split of tx.splits) {
        if (split.accountId === id && split.reconcileState !== 'y') {
          list.push({
            txId: tx.id,
            txDate: tx.date,
            txDesc: tx.description,
            txNum: tx.num,
            splitId: split.splitId,
            amount: split.amount,
            cleared: split.reconcileState === 'c',
          });
        }
      }
    }
    list.sort((a, b) => a.txDate.localeCompare(b.txDate));
    return list;
  }, [ledger, id]);

  if (!account) {
    return (
      <div className="fixed inset-0 z-40 bg-bg flex flex-col">
        <header className="shrink-0 bg-bg-surface border-b border-border pt-safe">
          <div className="h-12 flex items-center px-2">
            <button
              onClick={() => navigate(-1)}
              aria-label={t('common:actions.close')}
              className="w-11 h-11 flex items-center justify-center text-text-muted active:text-text -ml-1"
            >
              <CloseIcon size={22} />
            </button>
            <h1 className="flex-1 text-md font-semibold text-text truncate">{t('accounting:glossary.reconcile')}</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-8 text-center text-text-muted">
          {t('transactions:register.accountNotFound')}
        </div>
      </div>
    );
  }

  // Parse the statement balance to minor units, honoring the currency's decimals.
  const decimals = getCurrencyDecimalPlaces(currency);
  const statementMinor =
    statementBalance.trim() !== ''
      ? Math.round(parseFloat(statementBalance) * Math.pow(10, decimals))
      : NaN;

  const clearedMinor = items
    .filter((i) => checkedSplitIds.has(i.splitId))
    .reduce((sum, i) => sum + i.amount, 0);

  const diff = isNaN(statementMinor) ? NaN : clearedMinor - statementMinor;
  const balanced = !isNaN(diff) && diff === 0;

  function toggleSplit(splitId: string) {
    void hapticMedium();
    setCheckedSplitIds((prev) => {
      const next = new Set(prev);
      if (next.has(splitId)) next.delete(splitId);
      else next.add(splitId);
      return next;
    });
  }

  function startReconciling() {
    if (isNaN(statementMinor)) {
      setError(t('reconcile.enterEndingBalance'));
      return;
    }
    setError('');
    setStep(2);
  }

  async function handleFinish() {
    if (!id || !balanced) return;
    setError('');
    setSaving(true);
    try {
      const payload: ReconciliationPostedPayload = {
        accountId: id,
        statementDate,
        statementBalance: statementMinor,
        reconciledSplitIds: [...checkedSplitIds],
      };
      await encryptAndPush(await buildEvent('ReconciliationPosted', payload, ledger.lastSequence + 1));
      await hapticSuccess();
      navigate(-1);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:postReconciliation'));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-bg flex flex-col">
      {/* Header */}
      <header className="shrink-0 bg-bg-surface border-b border-border pt-safe">
        <div className="h-12 flex items-center px-2 gap-1">
          <button
            onClick={() => (step === 2 ? setStep(1) : navigate(-1))}
            aria-label={step === 2 ? t('common:actions.back') : t('common:actions.close')}
            className="w-11 h-11 flex items-center justify-center text-text-muted active:text-text -ml-1"
          >
            <CloseIcon size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-md font-semibold text-text truncate">{account.name}</div>
            <div className="text-xs text-text-subtle">
              {step === 1 ? t('accounting:reconcile.statementBalance') : t('reconcile.clearTransactions')}
            </div>
          </div>
        </div>
      </header>

      {step === 1 ? (
        /* Step 1 — statement balance entry */
        <div className="flex-1 overflow-y-auto flex flex-col">
          <div className="p-4 space-y-6 max-w-md w-full mx-auto">
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wide mb-2">
                {t('reconcile.statementDate')}
              </label>
              <input
                type="date"
                value={statementDate}
                onChange={(e) => setStatementDate(e.target.value)}
                className="w-full bg-bg-surface border border-border text-text px-3 h-12 text-md focus:border-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wide mb-2">
                {t('reconcile.endingStatementBalance')}
              </label>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                autoFocus
                value={statementBalance}
                onChange={(e) => setStatementBalance(e.target.value)}
                placeholder="0.00"
                className="w-full bg-bg-surface border border-border text-text px-3 h-16 text-3xl text-right font-mono tabular-nums focus:border-accent focus:outline-none"
              />
              <p className="mt-2 text-xs text-text-subtle">
                {t('reconcile.enterClosingBalance', { currency })}
              </p>
            </div>
            {error && <p className="text-sm text-negative">{error}</p>}
          </div>

          <div className="mt-auto shrink-0 border-t border-border bg-bg-surface p-3 pb-safe">
            <button
              onClick={startReconciling}
              className="w-full h-12 bg-accent text-white text-md font-semibold active:opacity-80 transition-opacity"
            >
              {t('reconcile.startReconciling')}
            </button>
          </div>
        </div>
      ) : (
        /* Step 2 — single-column clearing list */
        <>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-16 px-8 text-center text-sm text-text-subtle">
                {t('reconcile.noUnreconciled')}
              </div>
            ) : (
              <ul>
                {items.map((item) => {
                  const checked = checkedSplitIds.has(item.splitId);
                  const isIn = item.amount > 0;
                  return (
                    <li key={item.splitId}>
                      <button
                        onClick={() => toggleSplit(item.splitId)}
                        className={`w-full min-h-[56px] flex items-center gap-3 px-4 py-2 text-left border-b border-border transition-colors ${
                          checked ? 'bg-accent-muted' : 'active:bg-bg-elevated'
                        }`}
                      >
                        <span className="w-6 flex items-center justify-center shrink-0">
                          <ReconcileMark state={checked ? 'y' : item.cleared ? 'c' : 'n'} size="md" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-md text-text truncate">
                            {item.txDesc || t('noDescription')}
                          </span>
                          <span className="block text-xs font-mono text-text-muted">
                            {formatDate(item.txDate, { month: '2-digit', day: '2-digit', year: '2-digit' })}
                            {item.txNum ? ` · ${item.txNum}` : ''}
                          </span>
                        </span>
                        <span
                          className={`shrink-0 font-mono tabular-nums text-md ${
                            isIn ? 'text-positive' : 'text-negative'
                          }`}
                        >
                          {isIn ? '+' : '−'}
                          {formatAmount(Math.abs(item.amount), currency)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Sticky bottom summary + Finish */}
          <div className="shrink-0 border-t border-border bg-bg-surface pb-safe">
            <div className="px-4 py-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">{t('accounting:reconcile.statementBalance')}</span>
                <span className="font-mono tabular-nums text-text">
                  {formatAmount(isNaN(statementMinor) ? 0 : statementMinor, currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">{t('accounting:reconcile.clearedBalance')}</span>
                <span className="font-mono tabular-nums text-text">
                  {formatAmount(clearedMinor, currency)}
                </span>
              </div>
              <div className="flex justify-between text-md font-semibold border-t border-border pt-1.5">
                <span className="text-text">{t('accounting:reconcile.difference')}</span>
                <span
                  className={`font-mono tabular-nums ${balanced ? 'text-positive' : 'text-negative'}`}
                >
                  {isNaN(diff)
                    ? '—'
                    : `${diff > 0 ? '+' : ''}${formatAmount(diff, currency)}`}
                </span>
              </div>
            </div>
            {error && <p className="px-4 pb-2 text-sm text-negative">{error}</p>}
            <div className="px-3 pb-3">
              <button
                onClick={handleFinish}
                disabled={!balanced || saving}
                className="w-full h-12 bg-positive text-white text-md font-semibold active:opacity-80 disabled:opacity-40 transition-opacity"
              >
                {saving ? t('reconcile.finishing') : t('reconcile.finish')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
